import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  Cloud, Sun, CloudRain, Wind, Thermometer, Droplets,
  AlertTriangle, CheckCircle, MapPin, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

export default function WeatherTraining({ defaultLocation }) {
  const [weather, setWeather] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState(defaultLocation || { lat: 51.5074, lon: -0.1278 }); // London default

  useEffect(() => {
    // Try to get user's location
    if (navigator.geolocation && !defaultLocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lon: position.coords.longitude
          });
        },
        () => {
          // Use default if permission denied
          fetchWeatherData(location.lat, location.lon);
        }
      );
    }
    fetchWeatherData(location.lat, location.lon);
  }, []);

  useEffect(() => {
    if (location.lat && location.lon) {
      fetchWeatherData(location.lat, location.lon);
    }
  }, [location]);

  const fetchWeatherData = async (lat, lon) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch weather and suggestions in parallel
      const [weatherRes, suggestionsRes] = await Promise.all([
        axios.get(`${API}/weather/current?latitude=${lat}&longitude=${lon}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/weather/training-suggestions?latitude=${lat}&longitude=${lon}&session_type=training`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setWeather(weatherRes.data);
      setSuggestions(suggestionsRes.data);
    } catch (err) {
      console.error('Failed to fetch weather:', err);
      toast.error('Failed to fetch weather data');
    } finally {
      setLoading(false);
    }
  };

  const getWeatherIcon = (description) => {
    const desc = description?.toLowerCase() || '';
    if (desc.includes('rain') || desc.includes('drizzle')) return <CloudRain className="h-8 w-8 text-blue-400" />;
    if (desc.includes('cloud')) return <Cloud className="h-8 w-8 text-gray-400" />;
    return <Sun className="h-8 w-8 text-yellow-400" />;
  };

  const getRecommendationColor = (rec) => {
    switch (rec) {
      case 'proceed': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'modified': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'caution': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-zinc-700 text-zinc-400 border-zinc-600';
    }
  };

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6" data-testid="weather-training">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl flex items-center justify-center">
            <Cloud className="h-5 w-5 text-cyan-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Weather & Training</h3>
            <p className="text-xs text-zinc-500">Smart training recommendations</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => fetchWeatherData(location.lat, location.lon)}
          disabled={loading}
          className="text-zinc-400 hover:text-white"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
        </Button>
      </div>

      {loading && !weather ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />
        </div>
      ) : weather ? (
        <div className="space-y-4">
          {/* Current Weather */}
          <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-xl">
            <div className="flex items-center gap-4">
              {getWeatherIcon(weather.description)}
              <div>
                <p className="text-3xl font-bold text-white">{Math.round(weather.temperature)}°C</p>
                <p className="text-sm text-zinc-400">{weather.description}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2 text-zinc-400">
                <Thermometer className="h-4 w-4" />
                Feels {Math.round(weather.feels_like)}°C
              </div>
              <div className="flex items-center gap-2 text-zinc-400">
                <Wind className="h-4 w-4" />
                {Math.round(weather.wind_speed)} km/h
              </div>
              <div className="flex items-center gap-2 text-zinc-400">
                <Droplets className="h-4 w-4" />
                {weather.humidity}%
              </div>
              {weather.precipitation > 0 && (
                <div className="flex items-center gap-2 text-blue-400">
                  <CloudRain className="h-4 w-4" />
                  {weather.precipitation}mm
                </div>
              )}
            </div>
          </div>

          {/* Training Recommendation */}
          {suggestions && (
            <div className={`p-4 rounded-xl border ${getRecommendationColor(suggestions.overall_recommendation)}`}>
              <div className="flex items-center gap-2 mb-2">
                {suggestions.overall_recommendation === 'proceed' ? (
                  <CheckCircle className="h-5 w-5" />
                ) : (
                  <AlertTriangle className="h-5 w-5" />
                )}
                <span className="font-medium capitalize">
                  {suggestions.overall_recommendation === 'proceed' ? 'Good to train' :
                   suggestions.overall_recommendation === 'modified' ? 'Train with modifications' :
                   'Exercise caution'}
                </span>
              </div>
            </div>
          )}

          {/* Warnings */}
          {suggestions?.warnings?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-zinc-400">Warnings</h4>
              {suggestions.warnings.map((warning, idx) => (
                <div key={idx} className={`p-3 rounded-lg flex items-start gap-2 ${
                  warning.severity === 'high' ? 'bg-red-500/10 text-red-400' : 'bg-amber-500/10 text-amber-400'
                }`}>
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span className="text-sm">{warning.message}</span>
                </div>
              ))}
            </div>
          )}

          {/* Suggestions */}
          {suggestions?.suggestions?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-zinc-400">Recommendations</h4>
              <ul className="space-y-1">
                {suggestions.suggestions.map((suggestion, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-zinc-300">
                    <CheckCircle className="h-4 w-4 text-green-400 mt-0.5 shrink-0" />
                    {suggestion}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Alternatives */}
          {suggestions?.alternatives?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-zinc-400">Alternatives</h4>
              {suggestions.alternatives.map((alt, idx) => (
                <div key={idx} className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm text-blue-300">
                  {alt.message}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8 text-zinc-500">
          <Cloud className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>Unable to load weather data</p>
        </div>
      )}
    </div>
  );
}
